<template>
    <div>
        <progress-bar v-model="percentageCompleted" type="info" striped label />
        <aristotle-file-pond
            ref="aristotlepond"
            :url="url"
            :status-url="statusUrl"
            :label-idle="labelIdle"
            :accepted-file-types="acceptedFileTypes"
            :file-validate-type-label-expected-types-map="fileValidateTypeLabelExpectedTypesMap"
            :fileValidateTypeDetectType="getCustomMIMEType"
            @processfileprogress="(file, progress) => changeProgress(progress)"
            @removefile="setInitialState"
            :server="{
                process: (fieldName, file, metadata, load, error) => postFile(file, load, error, true),
                revert: null,
                fetch: null,
            }"
            :current-tags="currentTags"
            :selected-work-group="selectedWorkGroup"
            :content-type="contentType"
            :item-id="itemId"
            :disabled="false"
            @changeProgress="changeProgress"
            @changeLoadingMessage="changeLoadingMessage"
            @changeNumberOfSteps="changeNumberOfSteps"
            @changeFileIsLoaded="changeFileIsLoaded"
            @changeErrors="changeErrors"
            @changeListOfMetadataRepresentations="changeListOfMetadataRepresentations"
            @changeIsLoading="changeIsLoading"
            @changeExcelToCsvMessage="changeExcelToCsvMessage"
            @changeShowLoginMessage="changeShowLoginMessage"
            @changeTags="changeTags"
            @changeSelectedWorkgroup="changeSelectedWorkgroup"
        />
<!--        TODO: Implement vue component for Distribution selection. We are waiting for Harry's PR. -->
<!--        <select v-model="selectedDistribution">-->
<!--            <option disabled value="">-->
<!--                Select a distribution-->
<!--            </option>-->
<!--            <option>A</option>-->
<!--            <option>B</option>-->
<!--            <option>C</option>-->
<!--        </select>-->
<!--        <span>Selected: {{ selectedDistribution }}</span>-->
        <file-uploader-error-display :errors="errors" />
        <div v-if="showLoginMessage" class="alert alert-info">
            Your file has been processed and the following metadata items were found in the file.
            You will need to log in to save these metadata items.
        </div>
        <div v-if="excelToCsvMessage" class="alert alert-info">
            {{ excelToCsvMessage }}
        </div>
        <sddf-templates
            v-if="percentageCompleted === 0"
            :excel-template-file-path="excelTemplateFilePath"
            :excel-example-file-path="excelExampleFilePath"
            :csv-template-file-path="csvTemplateFilePath"
            :csv-example-file-path="csvExampleFilePath"
        />
        <div class="row">
            <div class="col-sm-4">
                <file-uploader-options
                    v-if="fileIsLoaded"
                    :user-workgroups="userWorkgroups"
                    :user-tags="userTags"
                    :selected-work-group="selectedWorkGroup"
                    type="sddf"
                    @changeSelectedWorkgroup="changeSelectedWorkgroup"
                    @changeTags="changeTags"
                />
                <table-of-contents
                    :list-of-metadata-representations="cleanList(reversedListOfMetadataRepresentations)"
                />
            </div>
            <div class="col-sm-8">
                <button v-if="fileIsLoaded" class="btn btn-success pull-right" @click="submitFile">
                    {{ buttonMessage }}
                </button>
                <p v-if="fileIsLoaded">
                    Your data dictionary file has been processed and the following metadata items were found in the
                    file. Click "{{ buttonMessage }}" to save these items into the registry.
                </p>
                <p v-if="percentageCompleted === 100">
                    The objects provided in the file have been successfully created! Click on the links below to see
                    the items in a new tab.
                </p>
                <metadata-representation
                    :list-of-metadata-representations="cleanList(reversedListOfMetadataRepresentations)"
                />
                <button v-if="fileIsLoaded" class="btn btn-success pull-right" @click="submitFile">
                    {{ buttonMessage }}
                </button>
            </div>
            <loading v-if="isLoading" :loading-message="loadingMessage" />
        </div>
    </div>
</template>

<script>
import apiRequest from 'src/mixins/api-request.js'
import AristotleFilePond from "@/fileuploader/AristotleFilePond.vue"
import FileUploaderErrorDisplay from '@/fileuploader/FileUploaderErrorDisplay.vue'
import FileUploaderOptions from "@/fileuploader/FileUploaderOptions.vue"
import MetadataRepresentation from "@/fileuploader/MetadataRepresentation.vue"
import TableOfContents from "@/fileuploader/TableOfContents.vue"
import Loading from 'src/components/Loading.vue'
import Constants from "@/fileuploader/constants.js"
import { ProgressBar } from 'uiv'
import SddfTemplates from "@/sddf/SddfTemplates.vue"
import aristotleFilePondMixin from "@/fileuploader/aristotle-file-pond-mixin"


export default {
    name: "SddfFileUploader",
    components: {
        AristotleFilePond,
        FileUploaderErrorDisplay,
        FileUploaderOptions,
        MetadataRepresentation,
        TableOfContents,
        Loading,
        ProgressBar,
        SddfTemplates,
    },
    mixins: [apiRequest, aristotleFilePondMixin],
    props: {
        url: {
            type: String,
            required: true,
        },
        statusUrl: {
            type: String,
            required: true,
        },
        userTags: {
            type: String,
            required: true,
        },
        userWorkgroups: {
            type: String,
            required: true,
        },
        contentType: {
            type: String,
            default: '',
        },
        itemId: {
            type: String,
            default: '',
        },
        excelTemplateFilePath: {
            type: String,
            default: '',
        },
        excelExampleFilePath: {
            type: String,
            default: '',
        },
        csvTemplateFilePath: {
            type: String,
            default: '',
        },
        csvExampleFilePath: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        fileValidateTypeLabelExpectedTypesMap: Constants.fileValidateTypeLabelExpectedTypesMap,
        labelIdle: Constants.labelIdle,
        acceptedFileTypes: Constants.acceptedFileTypes,
        isLoading: false,
        fileIsLoaded: false,
        listOfMetadataRepresentations: [],
        buttonMessage: 'Save metadata',
        loadingMessage: 'Reading file...',
        pollTime: 1000,  // period to call checkStatus in ms
        timeout: 30000,  // period after which failure assumed if still pending
        percentageCompleted: 0,
        currentTags: [],
        selectedWorkGroup: '',
        excelToCsvMessage: '',
        numberOfSteps: 2,
        showLoginMessage: false,
        filePondError: null,
    }),
    computed: {
        reversedListOfMetadataRepresentations() {
            return  this.listOfMetadataRepresentations.slice().reverse()
        },
    },
    methods: {
        onError: function () {
            this.isLoading = false
        },
        cleanList: function (listOfMetadataRepresentations) {
            /**Remove the list header completely if there are no MetadataRepresentations for this type.*/
            let cleanList = []
            if (listOfMetadataRepresentations) {
                for (let metadataRepresentations of listOfMetadataRepresentations) {
                    if (metadataRepresentations.mapping.length) {
                        cleanList.push(metadataRepresentations)
                    }
                }
            }
            return cleanList
        },
    },
}
</script>

<style scoped>

</style>
